import { openSafeExternalUrl } from '../../utils/safeNavigation';
import { brandPalette as palette } from '../../utils/brandPalette';
// src/components/dashboard/SupportChat.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Avatar,
    Chip,
    IconButton,
    TextField,
    Button,
    Divider,
    Badge,
    Tooltip,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    InputAdornment,
    CircularProgress,
    Drawer,
    useTheme,
    useMediaQuery,
    alpha,
    Fade,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    LinearProgress,
    Alert,
    Snackbar,
    Card,
    CardContent,
    Stack,
    Skeleton,
    AvatarGroup,
    Collapse,
    Zoom,
    Rating,
    TextareaAutosize,
    SpeedDial,
    SpeedDialAction,
    SpeedDialIcon,
    Fab,
    LinearProgress as MuiLinearProgress,
} from '@mui/material';
import {
    Send as SendIcon,
    AttachFile as AttachFileIcon,
    Image as ImageIcon,
    EmojiEmotions as EmojiIcon,
    Menu as MoreVertIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Warning as ErrorIcon,
    SupportAgent as SupportAgentIcon,
    SupportAgent as HeadsetIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Message as MessageIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Verified as VerifiedIcon,
    Schedule as AccessTimeIcon,
    TaskAlt as DoneAllIcon,
    Check as DoneIcon,
    QuestionAnswer as QuestionAnswerIcon,
    QuestionAnswer as FeedbackIcon,
    Star as RateReviewIcon,
    AutoAwesome as AiIcon,
    EmojiEmotions as AddReactionIcon,
    Quickreply as ReplyIcon,
    ArrowForward as ForwardIcon,
    Delete as DeleteIcon,
    Warning as ReportIcon,
    Block as BlockIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Description as ContentCopyIcon,
    Link as LinkIcon,
    InsertDriveFile as FileIcon,
    Inventory2 as FolderOpenIcon,
    Send as SendTimeExtensionIcon,
    Quickreply as QuickReplyIcon,
    Psychology as BotIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Add as AddIcon,
    Star as StarIcon,
    RocketLaunch as RocketIcon,
    Handshake as HandshakeIcon,
    Payment as PaymentIcon,
    Settings as BuildIcon,
    FiberManualRecord as LiveIcon,
    ArrowForward as ArrowForwardIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    NotificationsActive as NotificationIcon,
    Chat as ChatIcon,
   
    Check as CheckIcon,
    CloudUpload as CloudUploadIcon,
    InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import Header from '../common/Header/Header';
import { db } from '../../config/firebase';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    serverTimestamp,
    Timestamp,
    limit,
    startAfter,
    endBefore,
    startAt,
    limitToLast,
    arrayUnion,
    arrayRemove,
    increment,
    setDoc,
    getCountFromServer,
    runTransaction,
    writeBatch,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { uploadFile } from '../../services/integrations/storage';

// ============================================
// DESIGN TOKENS — "Warm Ledger" editorial theme
// ============================================



const fontImport = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
`;

// ============================================
// ANIMATED SECTION WRAPPER
// ============================================

const AnimatedSection = ({ children, delay = 0 }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%' }}
        >
            {children}
        </motion.div>
    );
};

const SectionRule = ({ color = palette.rust }) => (
    <Box
        sx={{
            width: 40,
            height: 3,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${color}, transparent)`,
            mt: 0.75,
            animation: 'shimmerRule 3.2s ease-in-out infinite',
        }}
    />
);

// ============================================
// PAPER CARD STYLING - MATCHING DASHBOARD
// ============================================

const paperCardSx = {
    p: { xs: 2.5, md: 3 },
    borderRadius: '20px',
    border: `2px solid ${palette.hairline}`,
    boxShadow: '0 8px 32px rgba(33,26,20,0.10), 0 2px 8px rgba(33,26,20,0.06)',
    background: palette.paper,
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    boxSizing: 'border-box',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
    '&:hover': {
        boxShadow: '0 16px 48px rgba(33,26,20,0.14), 0 4px 16px rgba(33,26,20,0.08)',
        borderColor: `${palette.rust}50`,
        transform: 'translateY(-4px)',
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, ${palette.rust}, ${palette.gold}, ${palette.pine}, ${palette.purple}, ${palette.rust})`,
        backgroundSize: '300% 100%',
        opacity: 0.6,
        animation: 'shimmerEdge 10s linear infinite',
        pointerEvents: 'none',
        borderRadius: '20px 20px 0 0',
    },
};

// ============================================
// PANEL HEADER - MATCHING DASHBOARD
// ============================================

const PanelHeader = ({ icon, iconGradient, title, ruleColor, chipLabel, chipColor, action, onAction, mb = 2.5 }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
            <Box sx={{
                width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: iconGradient,
                color: '#fff',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                border: `2px solid ${alpha(palette.rust, 0.1)}`,
            }}>
                {React.cloneElement(icon, { sx: { fontSize: 20 } })}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, fontSize: '1.1rem', color: palette.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {title}
                </Typography>
                <SectionRule color={ruleColor} />
            </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {chipLabel && (
                <Chip
                    label={chipLabel}
                    size="small"
                    sx={{ height: 22, fontSize: '0.65rem', bgcolor: `${chipColor}12`, color: chipColor, fontWeight: 600, border: `1px solid ${chipColor}20` }}
                />
            )}
            {action && (
                <Button
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                    onClick={onAction}
                    sx={{ color: palette.rust, fontWeight: 600, textTransform: 'none', fontSize: '0.8rem' }}
                >
                    {action}
                </Button>
            )}
        </Box>
    </Box>
);

// ============================================
// MESSAGE TYPES & HELPERS
// ============================================

const MESSAGE_TYPES = {
    TEXT: 'text',
    IMAGE: 'image',
    FILE: 'file',
    SYSTEM: 'system',
    TYPING: 'typing',
    REACTION: 'reaction',
    DELETED: 'deleted',
};

const MESSAGE_STATUS = {
    SENT: 'sent',
    DELIVERED: 'delivered',
    READ: 'read',
    FAILED: 'failed',
};

const SUPPORT_CATEGORIES = [
    { id: 'general', label: 'General Help', icon: <HeadsetIcon />, color: palette.blue },
    { id: 'technical', label: 'Technical Support', icon: <BuildIcon />, color: palette.purple },
    { id: 'billing', label: 'Billing & Payments', icon: <PaymentIcon />, color: palette.gold },
    { id: 'account', label: 'Account Issues', icon: <PersonIcon />, color: palette.pine },
    { id: 'feature', label: 'Feature Requests', icon: <RocketIcon />, color: palette.teal },
    { id: 'partnership', label: 'Partnership', icon: <HandshakeIcon />, color: palette.rust },
];

const QUICK_REPLIES = [
    { id: '1', label: 'Thank you for your message. We\'ll get back to you shortly.' },
    { id: '2', label: 'I understand your concern. Let me check that for you.' },
    { id: '3', label: 'Could you please provide more details about the issue?' },
    { id: '4', label: 'I\'m happy to help! Let me look into this.' },
    { id: '5', label: 'Our team is working on this. We\'ll update you soon.' },
    { id: '6', label: 'I\'ve escalated this to our technical team.' },
];

const EMOJI_SET = [
    '😊', '😂', '🤣', '❤️', '💕', '💔', '🔥', '✨', '⭐', '🌟',
    '👍', '👎', '👏', '🙏', '💪', '🤝', '👋', '🙌', '🤗', '🤔',
    '😄', '😅', '😆', '😉', '😍', '🥰', '😘', '😗', '😙', '😚',
];

// ============================================
// PAGE LOADER
// ============================================

const PageLoader = () => (
    <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: palette.canvas,
        zIndex: 9999,
    }}>
        <style>{fontImport}</style>
        <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
        >
            <Box sx={{ position: 'relative', width: 80, height: 80 }}>
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                >
                    <Box sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        border: `3px solid ${palette.hairline}`,
                        borderTop: `3px solid ${palette.admin}`,
                        borderRight: `3px solid ${palette.rust}`,
                        borderBottom: `3px solid ${palette.gold}`,
                        borderLeft: `3px solid ${palette.pine}`,
                    }} />
                </motion.div>
                <SupportAgentIcon sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: 30,
                    color: palette.admin,
                }} />
            </Box>
        </motion.div>
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            sx={{ textAlign: 'center', mt: 3 }}
        >
            <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.2rem', color: palette.ink }}>
                Support Chat
            </Typography>
            <Box sx={{ width: 160, height: 3, bgcolor: palette.hairline, borderRadius: 2, mt: 1, overflow: 'hidden' }}>
                <motion.div
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 1.5 }}
                    style={{ height: '100%', background: `linear-gradient(90deg, ${palette.admin}, ${palette.rust})`, borderRadius: 2 }}
                />
            </Box>
        </motion.div>
    </Box>
);

// ============================================
// FLOATING ORBS - MATCHING DASHBOARD
// ============================================

const FloatingOrbs = () => {
    const orbs = [
        { color: palette.rust, size: 420, top: '-8%', left: '4%', duration: 26, delay: 0 },
        { color: palette.pine, size: 360, top: '18%', left: '82%', duration: 32, delay: 2 },
        { color: palette.gold, size: 300, top: '58%', left: '-6%', duration: 24, delay: 1 },
        { color: palette.purple, size: 340, top: '72%', left: '70%', duration: 30, delay: 3 },
        { color: palette.teal, size: 260, top: '38%', left: '46%', duration: 28, delay: 1.5 },
    ];

    return (
        <Box
            aria-hidden="true"
            sx={{
                position: 'fixed',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 0,
            }}
        >
            {orbs.map((orb, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{
                        opacity: [0.5, 0.85, 0.5],
                        x: [0, 40, -30, 0],
                        y: [0, -35, 25, 0],
                        scale: [1, 1.12, 0.94, 1],
                    }}
                    transition={{
                        duration: orb.duration,
                        delay: orb.delay,
                        repeat: Infinity,
                        ease: 'easeInOut',
                    }}
                    style={{
                        position: 'absolute',
                        top: orb.top,
                        left: orb.left,
                        width: orb.size,
                        height: orb.size,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${orb.color}22, ${orb.color}00 70%)`,
                        filter: 'blur(10px)',
                    }}
                />
            ))}
        </Box>
    );
};

// ============================================
// SUPPORT CHAT COMPONENT
// ============================================

const SupportChat = () => {
    const navigate = useNavigate();
    const { currentUser, userData, companyData } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // ============================================
    // STATE
    // ============================================

    const [loading, setLoading] = useState(true);
    const [tickets, setTickets] = useState([]);
    const [activeTicket, setActiveTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [typingUsers, setTypingUsers] = useState({});
    const [onlineAgents, setOnlineAgents] = useState([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showQuickReplies, setShowQuickReplies] = useState(false);
    const [showNewTicketDialog, setShowNewTicketDialog] = useState(false);
    const [newTicketCategory, setNewTicketCategory] = useState('general');
    const [newTicketSubject, setNewTicketSubject] = useState('');
    const [newTicketMessage, setNewTicketMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, complete, error
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [replyToMessage, setReplyToMessage] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [showTicketDetails, setShowTicketDetails] = useState(false);
    const [ticketStats, setTicketStats] = useState({
        total: 0,
        open: 0,
        inProgress: 0,
        resolved: 0,
        closed: 0,
        averageResponseTime: 0,
    });
    const [showRateDialog, setShowRateDialog] = useState(false);
    const [ratingValue, setRatingValue] = useState(0);
    const [ratingFeedback, setRatingFeedback] = useState('');
    const [ticketToRate, setTicketToRate] = useState(null);
    const [showWelcome, setShowWelcome] = useState(true);
    const [hasNewTicket, setHasNewTicket] = useState(false);
    const [uploadedFileNames, setUploadedFileNames] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const chatContainerRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    // ============================================
    // GET USER/COMPANY ID
    // ============================================

    const getCompanyId = useCallback(() => {
        return companyData?.companyId || userData?.companyId || companyData?.id || userData?.id || currentUser?.uid;
    }, [companyData, userData, currentUser]);

    const getCompanyName = useCallback(() => {
        return companyData?.companyName || userData?.companyName || companyData?.name || userData?.displayName || currentUser?.displayName || 'User';
    }, [companyData, userData, currentUser]);

    const getUserId = useCallback(() => {
        return currentUser?.uid || 'anonymous';
    }, [currentUser]);

    // ============================================
    // FETCH SUPPORT TICKETS
    // ============================================

    const fetchTickets = useCallback(async () => {
        try {
            const companyId = getCompanyId();
            const userId = getUserId();
            
            if (!companyId && !userId) return;

            const ticketsRef = collection(db, 'supportTickets');
            let q;

            if (companyId) {
                q = query(
                    ticketsRef,
                    where('companyId', '==', companyId),
                    orderBy('createdAt', 'desc')
                );
            } else {
                q = query(
                    ticketsRef,
                    where('userId', '==', userId),
                    orderBy('createdAt', 'desc')
                );
            }

            const snapshot = await getDocs(q);
            const ticketList = [];
            let stats = {
                total: 0,
                open: 0,
                inProgress: 0,
                resolved: 0,
                closed: 0,
                averageResponseTime: 0,
            };

            snapshot.forEach((doc) => {
                const data = doc.data();
                const ticket = { id: doc.id, ...data };
                ticketList.push(ticket);
                stats.total++;

                switch (data.status) {
                    case 'open': stats.open++; break;
                    case 'in_progress': stats.inProgress++; break;
                    case 'resolved': stats.resolved++; break;
                    case 'closed': stats.closed++; break;
                    default: stats.open++;
                }

                if (data.firstResponseAt && data.createdAt) {
                    const created = data.createdAt.toDate?.() || new Date(data.createdAt);
                    const responded = data.firstResponseAt.toDate?.() || new Date(data.firstResponseAt);
                    const diffMinutes = (responded - created) / (1000 * 60);
                    stats.averageResponseTime += diffMinutes;
                }
            });

            if (stats.total > 0) {
                stats.averageResponseTime = Math.round(stats.averageResponseTime / stats.total);
            }

            setTicketStats(stats);
            setTickets(ticketList);

            if (ticketList.length > 0) {
                const openTicket = ticketList.find(t => t.status === 'open' || t.status === 'in_progress');
                if (openTicket) {
                    setActiveTicket(openTicket);
                    fetchMessages(openTicket.id);
                } else {
                    setActiveTicket(ticketList[0]);
                    fetchMessages(ticketList[0].id);
                }
            } else {
                setActiveTicket(null);
                setMessages([]);
                setShowWelcome(true);
            }

        } catch (error) {
            console.error('Error fetching tickets:', error);
            toast.error('Failed to load support tickets');
        }
    }, [getCompanyId, getUserId]);

    // ============================================
    // FETCH MESSAGES FOR A TICKET
    // ============================================

    const fetchMessages = useCallback(async (ticketId) => {
        if (!ticketId) return;

        try {
            const messagesRef = collection(db, 'supportTickets', ticketId, 'messages');
            const q = query(messagesRef, orderBy('createdAt', 'asc'));

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const messageList = [];
                snapshot.forEach((doc) => {
                    messageList.push({ id: doc.id, ...doc.data() });
                });
                setMessages(messageList);

                const unreadMessages = messageList.filter(
                    m => !m.read && m.senderId !== getUserId()
                );
                if (unreadMessages.length > 0) {
                    void (async () => {
                        try {
                            for (let offset = 0; offset < unreadMessages.length; offset += 450) {
                                const batch = writeBatch(db);
                                unreadMessages.slice(offset, offset + 450).forEach(msg => {
                                    const msgRef = doc(db, 'supportTickets', ticketId, 'messages', msg.id);
                                    batch.update(msgRef, { read: true, readAt: serverTimestamp() });
                                });
                                await batch.commit();
                            }
                        } catch (readError) {
                            console.warn('Could not mark support messages read:', readError?.message || readError);
                        }
                    })();
                }
            });

            const ticketRef = doc(db, 'supportTickets', ticketId);
            const ticketUnsubscribe = onSnapshot(ticketRef, (doc) => {
                if (doc.exists()) {
                    const updatedTicket = { id: doc.id, ...doc.data() };
                    setActiveTicket(updatedTicket);
                    setTickets(prev => 
                        prev.map(t => t.id === updatedTicket.id ? updatedTicket : t)
                    );
                }
            });

            return () => {
                unsubscribe();
                ticketUnsubscribe();
            };
        } catch (error) {
            console.error('Error fetching messages:', error);
            toast.error('Failed to load messages');
        }
    }, [getUserId]);

    // ============================================
    // LISTEN FOR TYPING INDICATORS
    // ============================================

    useEffect(() => {
        if (!activeTicket) return;

        const typingRef = doc(db, 'supportTickets', activeTicket.id, 'typing', 'status');
        const unsubscribe = onSnapshot(typingRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                const now = Date.now();
                const typingTime = data.updatedAt?.toDate?.()?.getTime() || now;
                
                if (now - typingTime < 5000) {
                    setTypingUsers(prev => ({
                        ...prev,
                        [data.userId]: { name: data.userName, timestamp: typingTime }
                    }));
                } else {
                    setTypingUsers(prev => {
                        const newState = { ...prev };
                        delete newState[data.userId];
                        return newState;
                    });
                }
            }
        });

        return () => unsubscribe();
    }, [activeTicket]);

    // ============================================
    // LISTEN FOR ONLINE AGENTS
    // ============================================

    useEffect(() => {
        const agentsRef = collection(db, 'supportAgents');
        const q = query(agentsRef, where('online', '==', true));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const agentList = [];
            snapshot.forEach((doc) => {
                agentList.push({ id: doc.id, ...doc.data() });
            });
            setOnlineAgents(agentList);
        });

        return () => unsubscribe();
    }, []);

    // ============================================
    // SEND MESSAGE WITH PROGRESS
    // ============================================

    const sendMessage = async (text, attachments = [], replyTo = null) => {
        if (!text.trim() && attachments.length === 0) return;
        if (!activeTicket) {
            toast.error('No active support ticket');
            return;
        }

        setIsSending(true);
        try {
            const userId = getUserId();
            const userName = getCompanyName();
            const companyId = getCompanyId();

            const messageData = {
                text: text.trim(),
                senderId: userId,
                senderName: userName,
                senderType: 'company',
                companyId: companyId,
                timestamp: serverTimestamp(),
                createdAt: serverTimestamp(),
                read: false,
                status: MESSAGE_STATUS.SENT,
                attachments: attachments,
                replyTo: replyTo,
            };

            const messagesRef = collection(db, 'supportTickets', activeTicket.id, 'messages');
            const docRef = await addDoc(messagesRef, messageData);

            const ticketRef = doc(db, 'supportTickets', activeTicket.id);
            await updateDoc(ticketRef, {
                lastMessage: text.trim().slice(0, 100),
                lastMessageAt: serverTimestamp(),
                lastMessageBy: userName,
                updatedAt: serverTimestamp(),
                ...(activeTicket.status === 'open' && !activeTicket.firstResponseAt ? {
                    firstResponseAt: serverTimestamp(),
                } : {}),
            });

            if (activeTicket.status === 'open') {
                await updateDoc(ticketRef, {
                    status: 'in_progress',
                });
                setActiveTicket(prev => ({ ...prev, status: 'in_progress' }));
            }

            setNewMessage('');
            setReplyToMessage(null);
            setUploadedFileNames([]);
            scrollToBottom();

            const typingRef = doc(db, 'supportTickets', activeTicket.id, 'typing', 'status');
            await setDoc(typingRef, {
                userId: userId,
                userName: userName,
                isTyping: false,
                updatedAt: serverTimestamp(),
            });

        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        } finally {
            setIsSending(false);
            setUploadStatus('idle');
        }
    };

    // ============================================
    // HANDLE FILE UPLOAD WITH PROGRESS
    // ============================================

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        setUploadStatus('uploading');
        setUploadProgress(0);
        setUploadedFileNames(files.map(f => f.name));

        try {
            const uploadedFiles = [];
            let completed = 0;
            const totalFiles = files.length;

            const companyId = getCompanyId();
            for (const file of files) {
                const kind = file.type?.startsWith('image/') ? 'image' : file.type?.startsWith('video/') ? 'video' : file.type?.startsWith('audio/') ? 'audio' : 'document';
                const asset = await uploadFile({
                    companyId,
                    file,
                    kind,
                    folder: `support/${activeTicket?.id || 'draft'}`,
                    onProgress: (progress) => {
                        const overallProgress = ((completed + (progress / 100)) / totalFiles) * 100;
                        setUploadProgress(Math.round(overallProgress));
                    }
                });
                uploadedFiles.push({
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    url: asset.url,
                    path: asset.path || asset.publicId || '',
                    asset,
                });
                completed++;
                setUploadProgress(Math.round((completed / totalFiles) * 100));
            }

            setUploadStatus('complete');
            
            // Send message with attachments if we have an active ticket
            if (activeTicket) {
                await sendMessage('', uploadedFiles);
                toast.success(`${uploadedFiles.length} file(s) uploaded and sent`);
            } else {
                toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
            }

        } catch (error) {
            console.error('Error uploading files:', error);
            setUploadStatus('error');
            toast.error('Failed to upload files');
        } finally {
            setIsUploading(false);
            setUploadStatus('idle');
            setUploadProgress(0);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // ============================================
    // HANDLE TYPING
    // ============================================

    const handleTyping = async (isTypingNow) => {
        if (!activeTicket) return;

        const userId = getUserId();
        const userName = getCompanyName();

        try {
            const typingRef = doc(db, 'supportTickets', activeTicket.id, 'typing', 'status');
            await setDoc(typingRef, {
                userId: userId,
                userName: userName,
                isTyping: isTypingNow,
                updatedAt: serverTimestamp(),
            });

            if (isTypingNow) {
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = setTimeout(() => {
                    handleTyping(false);
                }, 3000);
            }
        } catch (error) {
            console.error('Error updating typing status:', error);
        }
    };

    // ============================================
    // CREATE NEW TICKET
    // ============================================

    const createNewTicket = async () => {
        if (!newTicketSubject.trim()) {
            toast.error('Please enter a subject');
            return;
        }
        if (!newTicketMessage.trim()) {
            toast.error('Please describe your issue');
            return;
        }

        try {
            const companyId = getCompanyId();
            const userId = getUserId();
            const companyName = getCompanyName();

            const ticketData = {
                subject: newTicketSubject.trim(),
                category: newTicketCategory,
                status: 'open',
                priority: 'normal',
                companyId: companyId || null,
                userId: userId,
                companyName: companyName,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                lastMessage: newTicketMessage.trim().slice(0, 100),
                lastMessageAt: serverTimestamp(),
                lastMessageBy: companyName,
                firstResponseAt: null,
                resolvedAt: null,
                closedAt: null,
                assignedTo: null,
                tags: [],
                attachments: [],
                rating: null,
                ratingFeedback: '',
                source: 'web',
                browserInfo: navigator.userAgent.slice(0, 100),
            };

            const ticketsRef = collection(db, 'supportTickets');
            const docRef = await addDoc(ticketsRef, ticketData);

            const messagesRef = collection(db, 'supportTickets', docRef.id, 'messages');
            await addDoc(messagesRef, {
                text: newTicketMessage.trim(),
                senderId: userId,
                senderName: companyName,
                senderType: 'company',
                companyId: companyId || null,
                timestamp: serverTimestamp(),
                createdAt: serverTimestamp(),
                read: false,
                status: MESSAGE_STATUS.SENT,
                attachments: [],
            });

            toast.success('Support ticket created! We\'ll get back to you soon.');
            setShowNewTicketDialog(false);
            setNewTicketSubject('');
            setNewTicketMessage('');
            setNewTicketCategory('general');
            setShowWelcome(false);

            await fetchTickets();

        } catch (error) {
            console.error('Error creating ticket:', error);
            toast.error('Failed to create support ticket');
        }
    };

    // ============================================
    // SCROLL TO BOTTOM
    // ============================================

    const scrollToBottom = useCallback(() => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }, []);

    // ============================================
    // FORMAT MESSAGE TIME
    // ============================================

    const formatMessageTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        if (days < 7) return `${days}d ago`;
        return date.toLocaleDateString();
    };

    const formatFullTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    };

    // ============================================
    // FILTER TICKETS
    // ============================================

    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = ticket.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = filterCategory === 'all' || ticket.category === filterCategory;
        const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
        return matchesSearch && matchesCategory && matchesStatus;
    });

    // ============================================
    // GET TICKET STATUS COLOR
    // ============================================

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return palette.orange;
            case 'in_progress': return palette.blue;
            case 'resolved': return palette.success;
            case 'closed': return palette.inkSoft;
            default: return palette.inkSoft;
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'open': return 'Open';
            case 'in_progress': return 'In Progress';
            case 'resolved': return 'Resolved';
            case 'closed': return 'Closed';
            default: return status;
        }
    };

    // ============================================
    // HANDLE RATE TICKET
    // ============================================

    const handleRateTicket = async () => {
        if (!ticketToRate || ratingValue === 0) return;

        try {
            const ticketRef = doc(db, 'supportTickets', ticketToRate.id);
            await updateDoc(ticketRef, {
                rating: ratingValue,
                ratingFeedback: ratingFeedback,
                ratedAt: serverTimestamp(),
            });

            toast.success('Thank you for your feedback! ⭐');
            setShowRateDialog(false);
            setRatingValue(0);
            setRatingFeedback('');
            setTicketToRate(null);
            
            await fetchTickets();
        } catch (error) {
            console.error('Error rating ticket:', error);
            toast.error('Failed to submit rating');
        }
    };

    // ============================================
    // INITIAL SETUP
    // ============================================

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await fetchTickets();
            setLoading(false);
        };
        init();
    }, [fetchTickets]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // ============================================
    // RENDER: MESSAGE BUBBLE
    // ============================================

    const renderMessage = (message, index) => {
        const isOwn = message.senderId === getUserId();
        const isAdmin = message.senderType === 'admin';
        const isSystem = message.type === MESSAGE_TYPES.SYSTEM;
        const isDeleted = message.type === MESSAGE_TYPES.DELETED;

        if (isSystem) {
            return (
                <Box sx={{ textAlign: 'center', my: 2, px: 2 }}>
                    <Typography variant="caption" sx={{ 
                        color: palette.inkSoft, 
                        fontSize: '0.7rem',
                        bgcolor: `${palette.inkSoft}08`,
                        px: 2,
                        py: 0.5,
                        borderRadius: '12px',
                        border: `1px solid ${palette.hairline}`,
                    }}>
                        {message.text}
                    </Typography>
                </Box>
            );
        }

        if (isDeleted) {
            return (
                <Box sx={{ textAlign: 'center', my: 1, px: 2 }}>
                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontStyle: 'italic', fontSize: '0.7rem' }}>
                        {isOwn ? 'You deleted this message' : 'Message deleted'}
                    </Typography>
                </Box>
            );
        }

        return (
            <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.2, delay: index * 0.02 }}
                style={{ maxWidth: '100%' }}
            >
                <Box sx={{
                    display: 'flex',
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    mb: 1.5,
                    px: 1,
                }}>
                    {!isOwn && (
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                mr: 1.5,
                                mt: 0.5,
                                bgcolor: isAdmin ? palette.admin : palette.rust,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                border: `2px solid ${isAdmin ? palette.admin : palette.rust}30`,
                            }}
                        >
                            {isAdmin ? 'A' : message.senderName?.charAt(0) || '?'}
                        </Avatar>
                    )}
                    <Box sx={{
                        maxWidth: isOwn ? '75%' : '80%',
                    }}>
                        {!isOwn && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, ml: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.75rem' }}>
                                    {isAdmin ? 'Support Agent' : message.senderName}
                                </Typography>
                                {isAdmin && (
                                    <Chip
                                        label="Admin"
                                        size="small"
                                        sx={{
                                            height: 16,
                                            fontSize: '0.5rem',
                                            bgcolor: `${palette.admin}15`,
                                            color: palette.admin,
                                            fontWeight: 700,
                                            borderRadius: '4px',
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                bgcolor: isOwn ? `${palette.rust}10` : palette.paper,
                                border: `2px solid ${isOwn ? `${palette.rust}20` : palette.hairline}`,
                                boxShadow: isOwn ? `0 2px 12px ${palette.rust}10` : '0 2px 8px rgba(33,26,20,0.04)',
                            }}
                        >
                            <Typography variant="body2" sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: palette.ink,
                                fontSize: '0.9rem',
                                lineHeight: 1.6,
                            }}>
                                {message.text}
                            </Typography>

                            {message.attachments && message.attachments.length > 0 && (
                                <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {message.attachments.map((file, idx) => (
                                        <Paper
                                            key={idx}
                                            elevation={0}
                                            sx={{
                                                p: 1,
                                                borderRadius: '10px',
                                                bgcolor: `${palette.inkSoft}06`,
                                                border: `1px solid ${palette.hairline}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    bgcolor: `${palette.inkSoft}10`,
                                                    transform: 'translateY(-2px)',
                                                }
                                            }}
                                            onClick={() => openSafeExternalUrl(file.url)}
                                        >
                                            {file.type?.startsWith('image/') ? (
                                                <Box
                                                    component="img"
                                                    src={file.url}
                                                    alt={file.name}
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '8px',
                                                        objectFit: 'cover',
                                                        border: `1px solid ${palette.hairline}`,
                                                    }}
                                                />
                                            ) : (
                                                <InsertDriveFileIcon sx={{ color: palette.teal, fontSize: 24 }} />
                                            )}
                                            <Box sx={{ minWidth: 0, maxWidth: 120 }}>
                                                <Typography variant="caption" sx={{
                                                    display: 'block',
                                                    fontWeight: 600,
                                                    fontSize: '0.65rem',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    color: palette.ink,
                                                }}>
                                                    {file.name}
                                                </Typography>
                                                <Typography variant="caption" sx={{
                                                    fontSize: '0.55rem',
                                                    color: palette.inkSoft,
                                                }}>
                                                    {(file.size / 1024).toFixed(0)} KB
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                                <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.6rem' }}>
                                    {formatMessageTime(message.createdAt)}
                                </Typography>
                                {isOwn && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {message.status === MESSAGE_STATUS.SENT && (
                                            <ScheduleIcon sx={{ fontSize: 12, color: palette.inkSoft }} />
                                        )}
                                        {message.status === MESSAGE_STATUS.DELIVERED && (
                                            <DoneIcon sx={{ fontSize: 12, color: palette.inkSoft }} />
                                        )}
                                        {message.status === MESSAGE_STATUS.READ && (
                                            <DoneAllIcon sx={{ fontSize: 12, color: palette.blue }} />
                                        )}
                                        {message.status === MESSAGE_STATUS.FAILED && (
                                            <ErrorIcon sx={{ fontSize: 12, color: palette.danger }} />
                                        )}
                                    </Box>
                                )}
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        setSelectedMessage(message);
                                        setMessageMenuAnchor(e.currentTarget);
                                    }}
                                    sx={{
                                        p: 0.25,
                                        color: palette.inkSoft,
                                        opacity: 0.4,
                                        transition: 'opacity 0.2s ease',
                                        '&:hover': { opacity: 1, color: palette.ink },
                                    }}
                                >
                                    <MoreVertIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Box>
                        </Paper>
                    </Box>
                    {isOwn && (
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                ml: 1.5,
                                mt: 0.5,
                                bgcolor: palette.rust,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                border: `2px solid ${palette.rust}30`,
                            }}
                        >
                            {getCompanyName().charAt(0)}
                        </Avatar>
                    )}
                </Box>
            </motion.div>
        );
    };

    // ============================================
    // RENDER: TICKET CARD
    // ============================================

    const renderTicketCard = (ticket) => {
        const isActive = activeTicket?.id === ticket.id;
        const statusColor = getStatusColor(ticket.status);

        return (
            <ListItemButton
                key={ticket.id}
                selected={isActive}
                onClick={() => {
                    setActiveTicket(ticket);
                    fetchMessages(ticket.id);
                    setShowWelcome(false);
                }}
                sx={{
                    borderRadius: '14px',
                    mb: 1,
                    p: 2,
                    border: `2px solid ${isActive ? `${palette.rust}40` : 'transparent'}`,
                    bgcolor: isActive ? `${palette.rust}05` : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: isActive ? `${palette.rust}08` : `${palette.inkSoft}04`,
                        transform: 'translateX(4px)',
                    },
                    '&.Mui-selected': {
                        bgcolor: `${palette.rust}08`,
                        '&:hover': { bgcolor: `${palette.rust}12` },
                    }
                }}
            >
                <ListItemAvatar>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                        sx={{
                            '& .MuiBadge-badge': {
                                bgcolor: statusColor,
                                boxShadow: `0 0 0 2px ${palette.paper}`,
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                            }
                        }}
                    >
                        <Avatar
                            sx={{
                                bgcolor: ticket.status === 'resolved' || ticket.status === 'closed' 
                                    ? palette.inkSoft 
                                    : palette.rust,
                                width: 40,
                                height: 40,
                                borderRadius: '12px',
                                border: `2px solid ${statusColor}30`,
                            }}
                        >
                            {ticket.companyName?.charAt(0) || '?'}
                        </Avatar>
                    </Badge>
                </ListItemAvatar>
                <ListItemText
                    disableTypography
                    primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" fontWeight={600} sx={{ 
                                color: palette.ink, 
                                fontSize: '0.85rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '70%',
                            }}>
                                {ticket.subject || 'Support Request'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.6rem', ml: 1 }}>
                                {formatMessageTime(ticket.createdAt)}
                            </Typography>
                        </Box>
                    }
                    secondary={
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip
                                    label={getStatusLabel(ticket.status)}
                                    size="small"
                                    sx={{
                                        height: 18,
                                        fontSize: '0.55rem',
                                        fontWeight: 600,
                                        bgcolor: `${statusColor}15`,
                                        color: statusColor,
                                        borderRadius: '4px',
                                        border: `1px solid ${statusColor}30`,
                                    }}
                                />
                                <Chip
                                    label={ticket.category || 'General'}
                                    size="small"
                                    sx={{
                                        height: 18,
                                        fontSize: '0.55rem',
                                        fontWeight: 500,
                                        bgcolor: `${palette.inkSoft}08`,
                                        color: palette.inkSoft,
                                        borderRadius: '4px',
                                        border: `1px solid ${palette.hairline}`,
                                    }}
                                />
                            </Box>
                            {ticket.lastMessage && (
                                <Typography variant="caption" sx={{ 
                                    color: palette.inkSoft, 
                                    fontSize: '0.7rem',
                                    display: 'block',
                                    mt: 0.5,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '90%',
                                }}>
                                    {ticket.lastMessage}
                                </Typography>
                            )}
                        </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                />
            </ListItemButton>
        );
    };

    // ============================================
    // RENDER
    // ============================================

    if (loading) {
        return <PageLoader />;
    }

    const chatAreaSx = {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 160px)',
        bgcolor: palette.paper,
        borderRadius: '24px',
        border: `2px solid ${palette.hairline}`,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(33,26,20,0.10)',
        position: 'relative',
    };

    const isAgentOnline = onlineAgents.length > 0;

    return (
        <Box className="erp-page-shell erp-internal-page" sx={{
            minHeight: '100vh',
            bgcolor: palette.canvas,
            position: 'relative',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            backgroundImage: `
                radial-gradient(circle at 8% 0%, rgba(139,92,246,0.06), transparent 40%),
                radial-gradient(circle at 96% 8%, rgba(193,84,31,0.04), transparent 38%),
                radial-gradient(circle at 50% 100%, rgba(184,132,42,0.03), transparent 42%)
            `,
        }}>
            <style>{fontImport}</style>
            
            <Header />

            <Container maxWidth={false} disableGutters sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, sm: 3, md: 4, lg: 6 }, position: 'relative', zIndex: 1 }}>
                
                {/* Page Header - Matching Dashboard */}
                <Box className="erp-page-hero" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                fontWeight: 600,
                                fontSize: { xs: '1.4rem', md: '1.8rem' },
                                color: palette.ink,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <SupportAgentIcon sx={{ color: palette.admin, fontSize: { xs: 28, md: 32 } }} />
                            Support Chat
                        </Typography>
                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.85rem', mt: 0.25 }}>
                            {isAgentOnline ? (
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: palette.success, display: 'inline-block', animation: 'pulse 2s infinite' }} />
                                    {onlineAgents.length} agent{onlineAgents.length > 1 ? 's' : ''} online
                                </Box>
                            ) : (
                                'No agents online right now'
                            )}
                        </Typography>
                        <SectionRule color={palette.admin} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={fetchTickets}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: palette.hairline,
                                color: palette.inkSoft,
                                '&:hover': { borderColor: palette.rust, color: palette.rust },
                            }}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<MessageIcon />}
                            onClick={() => setShowNewTicketDialog(true)}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                background: `linear-gradient(135deg, ${palette.admin}, ${palette.purple})`,
                                boxShadow: '0 4px 16px rgba(139,92,246,0.3)',
                                border: `2px solid ${alpha(palette.admin, 0.2)}`,
                                '&:hover': {
                                    boxShadow: '0 8px 24px rgba(139,92,246,0.4)',
                                    transform: 'translateY(-2px)',
                                }
                            }}
                        >
                            New Ticket
                        </Button>
                    </Box>
                </Box>

                {/* Stats Row - Matching Dashboard */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr 1fr' },
                    gap: 2,
                    mb: 3,
                }}>
                    {[
                        { label: 'Total Tickets', value: ticketStats.total, color: palette.inkSoft },
                        { label: 'Open', value: ticketStats.open, color: palette.orange },
                        { label: 'In Progress', value: ticketStats.inProgress, color: palette.blue },
                        { label: 'Resolved', value: ticketStats.resolved, color: palette.success },
                        { label: 'Avg Response', value: `${ticketStats.averageResponseTime}m`, color: palette.gold },
                    ].map((stat, idx) => (
                        <Paper
                            key={idx}
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: '14px',
                                bgcolor: palette.paper,
                                border: `2px solid ${palette.hairline}`,
                                textAlign: 'center',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: `${stat.color}40`,
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 4px 16px ${stat.color}15`,
                                }
                            }}
                        >
                            <Typography variant="h5" fontWeight={700} sx={{ color: stat.color, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                                {stat.value}
                            </Typography>
                            <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 500 }}>
                                {stat.label}
                            </Typography>
                        </Paper>
                    ))}
                </Box>

                {/* Main Chat Area */}
                <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 340px)', minHeight: 500 }}>
                    {/* Ticket List - Desktop */}
                    <Paper
                        elevation={0}
                        sx={{
                            width: isMobile ? 0 : 340,
                            minWidth: isMobile ? 0 : 340,
                            borderRadius: '24px',
                            border: `2px solid ${palette.hairline}`,
                            bgcolor: palette.paper,
                            overflow: 'hidden',
                            display: isMobile ? 'none' : 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 4px 16px rgba(33,26,20,0.06)',
                        }}
                    >
                        <Box sx={{ p: 2, borderBottom: `2px solid ${palette.hairline}` }}>
                            <TextField
                                placeholder="Search tickets..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                size="small"
                                fullWidth
                                slotProps={{ input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: palette.inkSoft, fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                } }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        bgcolor: palette.canvasAlt,
                                        '& fieldset': { border: 'none' },
                                    }
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                                <Chip
                                    label="All"
                                    size="small"
                                    onClick={() => setFilterStatus('all')}
                                    sx={{
                                        bgcolor: filterStatus === 'all' ? palette.rust : 'transparent',
                                        color: filterStatus === 'all' ? '#fff' : palette.inkSoft,
                                        fontWeight: 600,
                                        borderRadius: '8px',
                                        border: `2px solid ${filterStatus === 'all' ? palette.rust : palette.hairline}`,
                                        cursor: 'pointer',
                                        '&:hover': { transform: 'translateY(-1px)' },
                                    }}
                                />
                                {['open', 'in_progress', 'resolved', 'closed'].map((status) => (
                                    <Chip
                                        key={status}
                                        label={getStatusLabel(status)}
                                        size="small"
                                        onClick={() => setFilterStatus(status)}
                                        sx={{
                                            bgcolor: filterStatus === status ? getStatusColor(status) : 'transparent',
                                            color: filterStatus === status ? '#fff' : palette.inkSoft,
                                            fontWeight: 600,
                                            borderRadius: '8px',
                                            border: `2px solid ${filterStatus === status ? getStatusColor(status) : palette.hairline}`,
                                            cursor: 'pointer',
                                            '&:hover': { transform: 'translateY(-1px)' },
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                            {filteredTickets.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <QuestionAnswerIcon sx={{ fontSize: 40, color: palette.inkSoft, opacity: 0.2 }} />
                                    <Typography sx={{ color: palette.inkSoft, mt: 1 }}>
                                        {searchQuery ? 'No matching tickets' : 'No support tickets'}
                                    </Typography>
                                    <Button
                                        variant="text"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => setShowNewTicketDialog(true)}
                                        sx={{ mt: 1, color: palette.rust, fontWeight: 600 }}
                                    >
                                        Create New Ticket
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {filteredTickets.map(renderTicketCard)}
                                </Box>
                            )}
                        </Box>
                    </Paper>

                    {/* Chat Area - Matching Dashboard Style */}
                    <Paper
                        elevation={0}
                        sx={chatAreaSx}
                    >
                        {/* Chat Header */}
                        <Box sx={{
                            p: { xs: 2, md: 2.5 },
                            borderBottom: `2px solid ${palette.hairline}`,
                            bgcolor: palette.canvasAlt,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ 
                                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", 
                                        color: palette.ink,
                                        fontSize: { xs: '1rem', md: '1.1rem' },
                                    }}>
                                        {activeTicket ? activeTicket.subject || 'Support Chat' : 'Select a Ticket'}
                                    </Typography>
                                    {activeTicket && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Chip
                                                label={getStatusLabel(activeTicket.status)}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.55rem',
                                                    fontWeight: 600,
                                                    bgcolor: `${getStatusColor(activeTicket.status)}15`,
                                                    color: getStatusColor(activeTicket.status),
                                                    borderRadius: '4px',
                                                    border: `1px solid ${getStatusColor(activeTicket.status)}30`,
                                                }}
                                            />
                                            <Chip
                                                label={activeTicket.category || 'General'}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.55rem',
                                                    fontWeight: 500,
                                                    bgcolor: `${palette.inkSoft}08`,
                                                    color: palette.inkSoft,
                                                    borderRadius: '4px',
                                                    border: `1px solid ${palette.hairline}`,
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {activeTicket && (
                                    <>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowTicketDetails(true)}
                                                sx={{ color: palette.inkSoft, border: `2px solid ${palette.hairline}`, borderRadius: '10px', p: 0.75 }}
                                            >
                                                <InfoIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                        {activeTicket.status === 'resolved' && !activeTicket.rating && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<StarIcon />}
                                                onClick={() => {
                                                    setTicketToRate(activeTicket);
                                                    setShowRateDialog(true);
                                                }}
                                                sx={{
                                                    borderRadius: '10px',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    borderColor: palette.gold,
                                                    color: palette.gold,
                                                    fontSize: '0.7rem',
                                                    '&:hover': {
                                                        bgcolor: `${palette.gold}10`,
                                                        borderColor: palette.gold,
                                                    }
                                                }}
                                            >
                                                Rate
                                            </Button>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>

                        {/* Messages Area */}
                        <Box
                            ref={chatContainerRef}
                            sx={{
                                flex: 1,
                                overflow: 'auto',
                                p: { xs: 2, md: 3 },
                                bgcolor: palette.canvasAlt,
                                position: 'relative',
                            }}
                        >
                            {!activeTicket && !showWelcome ? (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <SupportAgentIcon sx={{ fontSize: 56, color: palette.inkSoft, opacity: 0.2 }} />
                                    <Typography sx={{ color: palette.inkSoft, fontSize: '1.1rem', mt: 2, fontWeight: 600 }}>
                                        Select a support ticket
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 0.5 }}>
                                        Choose from your existing tickets or create a new one
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => setShowNewTicketDialog(true)}
                                        sx={{
                                            mt: 3,
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            background: `linear-gradient(135deg, ${palette.admin}, ${palette.purple})`,
                                            boxShadow: '0 4px 16px rgba(139,92,246,0.3)',
                                            '&:hover': { boxShadow: '0 8px 24px rgba(139,92,246,0.4)', transform: 'translateY(-2px)' },
                                        }}
                                    >
                                        Create New Ticket
                                    </Button>
                                </Box>
                            ) : showWelcome && !activeTicket ? (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <motion.div
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <Box sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: '50%',
                                            bgcolor: `${palette.admin}10`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 3,
                                            border: `2px solid ${palette.admin}20`,
                                        }}>
                                            <SupportAgentIcon sx={{ fontSize: 40, color: palette.admin }} />
                                        </Box>
                                    </motion.div>
                                    <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.4rem', color: palette.ink }}>
                                        Welcome to Support
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 1, maxWidth: 400, mx: 'auto' }}>
                                        Need help? Create a support ticket and our team will get back to you as soon as possible.
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => setShowNewTicketDialog(true)}
                                            sx={{
                                                borderRadius: '12px',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                background: `linear-gradient(135deg, ${palette.admin}, ${palette.purple})`,
                                                boxShadow: '0 4px 16px rgba(139,92,246,0.3)',
                                                '&:hover': { boxShadow: '0 8px 24px rgba(139,92,246,0.4)', transform: 'translateY(-2px)' },
                                            }}
                                        >
                                            Create Ticket
                                        </Button>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4, flexWrap: 'wrap' }}>
                                        {SUPPORT_CATEGORIES.map((cat) => (
                                            <Chip
                                                key={cat.id}
                                                icon={cat.icon}
                                                label={cat.label}
                                                onClick={() => {
                                                    setNewTicketCategory(cat.id);
                                                    setShowNewTicketDialog(true);
                                                }}
                                                sx={{
                                                    borderRadius: '10px',
                                                    fontWeight: 500,
                                                    border: `2px solid ${cat.color}30`,
                                                    bgcolor: `${cat.color}05`,
                                                    color: cat.color,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: `${cat.color}15`,
                                                        transform: 'translateY(-2px)',
                                                    }
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    {messages.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 6 }}>
                                            <MessageIcon sx={{ fontSize: 40, color: palette.inkSoft, opacity: 0.2 }} />
                                            <Typography sx={{ color: palette.inkSoft, mt: 1 }}>
                                                No messages yet
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                Start the conversation by sending a message below
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <>
                                            {messages.map((message, index) => renderMessage(message, index))}
                                            {Object.keys(typingUsers).length > 0 && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 2, mb: 1 }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            bgcolor: palette.admin,
                                                            fontSize: '0.7rem',
                                                        }}
                                                    >
                                                        A
                                                    </Avatar>
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: palette.ink }}>
                                                                {Object.values(typingUsers)[0]?.name || 'Agent'} is typing
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                <motion.div
                                                                    animate={{ y: [0, -6, 0] }}
                                                                    transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                                                                >
                                                                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: palette.inkSoft }} />
                                                                </motion.div>
                                                                <motion.div
                                                                    animate={{ y: [0, -6, 0] }}
                                                                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                                                                >
                                                                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: palette.inkSoft }} />
                                                                </motion.div>
                                                                <motion.div
                                                                    animate={{ y: [0, -6, 0] }}
                                                                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                                                                >
                                                                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: palette.inkSoft }} />
                                                                </motion.div>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                            <div ref={messagesEndRef} />
                                        </>
                                    )}
                                </>
                            )}
                        </Box>

                        {/* Message Input - With Upload Progress */}
                        {activeTicket && (
                            <Box sx={{
                                p: { xs: 2, md: 2.5 },
                                borderTop: `2px solid ${palette.hairline}`,
                                bgcolor: palette.paper,
                                borderRadius: '0 0 24px 24px',
                            }}>
                                {/* File Upload Progress */}
                                {uploadStatus === 'uploading' && (
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: palette.inkSoft }}>
                                                Uploading files...
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: palette.rust }}>
                                                {uploadProgress}%
                                            </Typography>
                                        </Box>
                                        <MuiLinearProgress
                                            variant="determinate"
                                            value={uploadProgress}
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                                bgcolor: palette.hairline,
                                                '& .MuiLinearProgress-bar': {
                                                    background: `linear-gradient(90deg, ${palette.rust}, ${palette.gold})`,
                                                    borderRadius: 3,
                                                }
                                            }}
                                        />
                                        {uploadedFileNames.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                {uploadedFileNames.map((name, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={name}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.55rem',
                                                            bgcolor: `${palette.rust}08`,
                                                            color: palette.inkSoft,
                                                            border: `1px solid ${palette.hairline}`,
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {uploadStatus === 'error' && (
                                    <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                                        Failed to upload files. Please try again.
                                    </Alert>
                                )}

                                {uploadStatus === 'complete' && (
                                    <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>
                                        Files uploaded successfully!
                                    </Alert>
                                )}

                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                        <Tooltip title="Upload File">
                                            <IconButton
                                                size="small"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploadStatus === 'uploading'}
                                                sx={{
                                                    border: `2px solid ${palette.hairline}`,
                                                    borderRadius: '10px',
                                                    p: 0.75,
                                                    color: palette.inkSoft,
                                                    '&:hover': { borderColor: palette.rust, color: palette.rust, bgcolor: 'transparent' },
                                                    '&:disabled': { opacity: 0.4 }
                                                }}
                                            >
                                                <AttachFileIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Emoji">
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                sx={{
                                                    border: `2px solid ${palette.hairline}`,
                                                    borderRadius: '10px',
                                                    p: 0.75,
                                                    color: palette.inkSoft,
                                                    '&:hover': { borderColor: palette.gold, color: palette.gold, bgcolor: 'transparent' },
                                                }}
                                            >
                                                <EmojiIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Quick Replies">
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowQuickReplies(!showQuickReplies)}
                                                sx={{
                                                    border: `2px solid ${palette.hairline}`,
                                                    borderRadius: '10px',
                                                    p: 0.75,
                                                    color: palette.inkSoft,
                                                    '&:hover': { borderColor: palette.pine, color: palette.pine, bgcolor: 'transparent' },
                                                }}
                                            >
                                                <QuickReplyIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>

                                    <TextField
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                        placeholder="Type your message..."
                                        value={newMessage}
                                        onChange={(e) => {
                                            setNewMessage(e.target.value);
                                            handleTyping(e.target.value.length > 0);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey && !isSending) {
                                                e.preventDefault();
                                                sendMessage(newMessage);
                                            }
                                        }}
                                        variant="outlined"
                                        size="small"
                                        disabled={uploadStatus === 'uploading'}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '14px',
                                                bgcolor: palette.canvasAlt,
                                                '& fieldset': { border: `2px solid ${palette.hairline}` },
                                                '&:hover fieldset': { borderColor: `${palette.rust}50` },
                                                '&.Mui-focused fieldset': { borderColor: palette.rust },
                                            }
                                        }}
                                    />

                                    <Button
                                        variant="contained"
                                        disableElevation
                                        onClick={() => sendMessage(newMessage)}
                                        disabled={(!newMessage.trim() && uploadStatus !== 'uploading') || isSending || uploadStatus === 'uploading'}
                                        sx={{
                                            borderRadius: '14px',
                                            minWidth: 50,
                                            height: 48,
                                            px: 2,
                                            background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                            boxShadow: '0 4px 12px rgba(193,84,31,0.3)',
                                            '&:hover': {
                                                boxShadow: '0 8px 20px rgba(193,84,31,0.4)',
                                                transform: 'translateY(-2px)',
                                            },
                                            '&:disabled': {
                                                bgcolor: palette.hairline,
                                                color: palette.inkSoft,
                                            }
                                        }}
                                    >
                                        {isSending ? (
                                            <CircularProgress size={20} sx={{ color: '#fff' }} />
                                        ) : (
                                            <SendIcon />
                                        )}
                                    </Button>
                                </Box>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handleFileUpload}
                                    accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx,.zip,.rar"
                                />

                                {/* Emoji Picker */}
                                <Collapse in={showEmojiPicker}>
                                    <Box sx={{
                                        mt: 1.5,
                                        p: 1.5,
                                        bgcolor: palette.canvasAlt,
                                        borderRadius: '12px',
                                        border: `2px solid ${palette.hairline}`,
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                        maxHeight: 120,
                                        overflow: 'auto',
                                    }}>
                                        {EMOJI_SET.map((emoji) => (
                                            <Button
                                                key={emoji}
                                                size="small"
                                                onClick={() => {
                                                    setNewMessage(prev => prev + emoji);
                                                    setShowEmojiPicker(false);
                                                }}
                                                sx={{
                                                    minWidth: 32,
                                                    height: 32,
                                                    p: 0,
                                                    fontSize: '1.2rem',
                                                    borderRadius: '8px',
                                                    '&:hover': { bgcolor: `${palette.inkSoft}08` },
                                                }}
                                            >
                                                {emoji}
                                            </Button>
                                        ))}
                                    </Box>
                                </Collapse>

                                {/* Quick Replies */}
                                <Collapse in={showQuickReplies}>
                                    <Box sx={{
                                        mt: 1.5,
                                        p: 1.5,
                                        bgcolor: palette.canvasAlt,
                                        borderRadius: '12px',
                                        border: `2px solid ${palette.hairline}`,
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}>
                                        {QUICK_REPLIES.map((reply) => (
                                            <Chip
                                                key={reply.id}
                                                label={reply.label}
                                                onClick={() => {
                                                    setNewMessage(reply.label);
                                                    setShowQuickReplies(false);
                                                }}
                                                sx={{
                                                    borderRadius: '10px',
                                                    bgcolor: palette.paper,
                                                    border: `2px solid ${palette.hairline}`,
                                                    '&:hover': {
                                                        borderColor: palette.pine,
                                                        bgcolor: `${palette.pine}05`,
                                                        transform: 'translateY(-2px)',
                                                    },
                                                    maxWidth: '100%',
                                                    '& .MuiChip-label': {
                                                        whiteSpace: 'normal',
                                                        fontSize: '0.7rem',
                                                    }
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Collapse>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Container>

            {/* Dialogs - Same as before */}
            {/* New Ticket Dialog */}
            <Dialog
                open={showNewTicketDialog}
                onClose={() => setShowNewTicketDialog(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: {
                    sx: {
                        borderRadius: '20px',
                        border: `2px solid ${palette.hairline}`,
                        boxShadow: '0 24px 64px rgba(33,26,20,0.12)',
                        background: palette.paper,
                        p: 1,
                    }
                } }}
            >
                <DialogTitle sx={{
                    borderBottom: `2px solid ${palette.hairline}`,
                    pb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                    fontWeight: 600,
                    color: palette.ink,
                }}>
                    <Box sx={{ alignItems: 'center', gap: 1.5, display: 'flex' }}>
                        <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${palette.admin}, ${palette.purple})`,
                            color: '#fff',
                        }}>
                            <SupportAgentIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700}>Create Support Ticket</Typography>
                    </Box>
                    <IconButton onClick={() => setShowNewTicketDialog(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={2.5}>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600, display: 'block', mb: 1 }}>
                                Category
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {SUPPORT_CATEGORIES.map((cat) => (
                                    <Chip
                                        key={cat.id}
                                        icon={cat.icon}
                                        label={cat.label}
                                        onClick={() => setNewTicketCategory(cat.id)}
                                        sx={{
                                            borderRadius: '10px',
                                            fontWeight: 500,
                                            border: `2px solid ${newTicketCategory === cat.id ? cat.color : palette.hairline}`,
                                            bgcolor: newTicketCategory === cat.id ? `${cat.color}15` : 'transparent',
                                            color: newTicketCategory === cat.id ? cat.color : palette.inkSoft,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 4px 12px ${cat.color}20`,
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Subject"
                                placeholder="Brief summary of your issue"
                                value={newTicketSubject}
                                onChange={(e) => setNewTicketSubject(e.target.value)}
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        '& fieldset': { border: `2px solid ${palette.hairline}` },
                                        '&:hover fieldset': { borderColor: `${palette.rust}50` },
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Message"
                                placeholder="Describe your issue in detail..."
                                value={newTicketMessage}
                                onChange={(e) => setNewTicketMessage(e.target.value)}
                                multiline
                                rows={5}
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        '& fieldset': { border: `2px solid ${palette.hairline}` },
                                        '&:hover fieldset': { borderColor: `${palette.rust}50` },
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `2px solid ${palette.hairline}`, gap: 2 }}>
                    <Button
                        onClick={() => setShowNewTicketDialog(false)}
                        sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={createNewTicket}
                        disabled={!newTicketSubject.trim() || !newTicketMessage.trim()}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 4,
                            py: 1.2,
                            background: `linear-gradient(135deg, ${palette.admin}, ${palette.purple})`,
                            boxShadow: '0 4px 16px rgba(139,92,246,0.3)',
                            '&:hover': {
                                boxShadow: '0 8px 24px rgba(139,92,246,0.4)',
                                transform: 'translateY(-2px)',
                            },
                            '&:disabled': { bgcolor: palette.hairline, color: palette.inkSoft }
                        }}
                    >
                        Submit Ticket
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rate Dialog */}
            <Dialog
                open={showRateDialog}
                onClose={() => setShowRateDialog(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: {
                    sx: {
                        borderRadius: '20px',
                        border: `2px solid ${palette.hairline}`,
                        boxShadow: '0 24px 64px rgba(33,26,20,0.12)',
                        background: palette.paper,
                    }
                } }}
            >
                <DialogTitle sx={{
                    borderBottom: `2px solid ${palette.hairline}`,
                    pb: 2,
                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                    fontWeight: 600,
                    color: palette.ink,
                    textAlign: 'center',
                }}>
                    Rate Your Support Experience
                </DialogTitle>
                <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                        <Rating
                            value={ratingValue}
                            onChange={(e, val) => setRatingValue(val || 0)}
                            size="large"
                            sx={{
                                '& .MuiRating-icon': { fontSize: 40, mx: 0.5 },
                                '& .MuiRating-iconFilled': { color: palette.gold },
                            }}
                        />
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Feedback (optional)"
                        placeholder="Tell us about your experience..."
                        value={ratingFeedback}
                        onChange={(e) => setRatingFeedback(e.target.value)}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                '& fieldset': { border: `2px solid ${palette.hairline}` },
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `2px solid ${palette.hairline}`, gap: 2, justifyContent: 'center' }}>
                    <Button
                        onClick={() => setShowRateDialog(false)}
                        sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}
                    >
                        Skip
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRateTicket}
                        disabled={ratingValue === 0}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 4,
                            py: 1.2,
                            background: `linear-gradient(135deg, ${palette.gold}, #d4a13f)`,
                            boxShadow: '0 4px 16px rgba(184,132,42,0.3)',
                            '&:hover': {
                                boxShadow: '0 8px 24px rgba(184,132,42,0.4)',
                                transform: 'translateY(-2px)',
                            },
                            '&:disabled': { bgcolor: palette.hairline, color: palette.inkSoft }
                        }}
                    >
                        Submit Rating
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Ticket Details Dialog */}
            <Dialog
                open={showTicketDetails}
                onClose={() => setShowTicketDetails(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: {
                    sx: {
                        borderRadius: '20px',
                        border: `2px solid ${palette.hairline}`,
                        boxShadow: '0 24px 64px rgba(33,26,20,0.12)',
                        background: palette.paper,
                    }
                } }}
            >
                {activeTicket && (
                    <>
                        <DialogTitle sx={{
                            borderBottom: `2px solid ${palette.hairline}`,
                            pb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                            fontWeight: 600,
                            color: palette.ink,
                        }}>
                            <Box sx={{ alignItems: 'center', gap: 1.5, display: 'flex' }}>
                                <InfoIcon sx={{ color: palette.admin }} />
                                Ticket Details
                            </Box>
                            <IconButton onClick={() => setShowTicketDetails(false)}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ pt: 3 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.inkSoft, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                        Subject
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: palette.ink, mt: 0.5 }}>
                                        {activeTicket.subject}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.inkSoft, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                        Status
                                    </Typography>
                                    <Chip
                                        label={getStatusLabel(activeTicket.status)}
                                        size="small"
                                        sx={{
                                            mt: 0.5,
                                            bgcolor: `${getStatusColor(activeTicket.status)}15`,
                                            color: getStatusColor(activeTicket.status),
                                            fontWeight: 600,
                                            border: `1px solid ${getStatusColor(activeTicket.status)}30`,
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.inkSoft, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                        Category
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: palette.ink, mt: 0.5, fontWeight: 500 }}>
                                        {activeTicket.category || 'General'}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.inkSoft, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                        Created
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: palette.ink, mt: 0.5 }}>
                                        {formatFullTime(activeTicket.createdAt)}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.inkSoft, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                        Updated
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: palette.ink, mt: 0.5 }}>
                                        {formatFullTime(activeTicket.updatedAt)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{ p: 3, borderTop: `2px solid ${palette.hairline}`, gap: 2 }}>
                            <Button
                                onClick={() => setShowTicketDetails(false)}
                                sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Message Context Menu */}
            <Menu
                anchorEl={messageMenuAnchor}
                open={Boolean(messageMenuAnchor)}
                onClose={() => setMessageMenuAnchor(null)}
                slotProps={{ paper: {
                    sx: {
                        borderRadius: '14px',
                        border: `2px solid ${palette.hairline}`,
                        boxShadow: '0 12px 40px rgba(33,26,20,0.1)',
                        minWidth: 180,
                    }
                } }}
            >
                <MenuItem onClick={() => {
                    if (selectedMessage) {
                        setReplyToMessage(selectedMessage);
                        setMessageMenuAnchor(null);
                    }
                }}>
                    <ReplyIcon sx={{ fontSize: 18, mr: 1.5 }} /> Reply
                </MenuItem>
                <MenuItem onClick={() => {
                    if (selectedMessage?.text) {
                        navigator.clipboard.writeText(selectedMessage.text);
                        toast.success('Copied to clipboard');
                        setMessageMenuAnchor(null);
                    }
                }}>
                    <ContentCopyIcon sx={{ fontSize: 18, mr: 1.5 }} /> Copy
                </MenuItem>
                {selectedMessage?.attachments?.length > 0 && (
                    <MenuItem onClick={() => {
                        if (selectedMessage?.attachments?.[0]?.url) {
                            openSafeExternalUrl(selectedMessage.attachments[0].url);
                            setMessageMenuAnchor(null);
                        }
                    }}>
                        <DownloadIcon sx={{ fontSize: 18, mr: 1.5 }} /> Download
                    </MenuItem>
                )}
                <Divider />
                <MenuItem sx={{ color: palette.danger }}>
                    <ReportIcon sx={{ fontSize: 18, mr: 1.5 }} /> Report
                </MenuItem>
            </Menu>

            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.5; transform: scale(0.8); }
                    }
                    @keyframes float {
                        0%, 100% { transform: translate(0, 0) rotate(0deg); }
                        50% { transform: translate(-10px, -10px) rotate(5deg); }
                    }
                    @keyframes shimmerRule {
                        0%, 100% { opacity: 0.7; width: 40px; }
                        50% { opacity: 1; width: 56px; }
                    }
                    @keyframes shimmerEdge {
                        0% { background-position: 0% 0%; }
                        100% { background-position: 300% 0%; }
                    }
                `}
            </style>
        </Box>
    );
};

export default SupportChat;